/* Added the following code for FAST.Ray using AI Agent. 27/07/2026. Code Start */
/**
 * Compact progress rail for the left sidebar (Resside).
 * Shows chapter %, module checklist, next action / next topic, and section pages.
 * Accordion jump uses stable data-accordion-id from the API checklist.
 */
import React, { useId, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { BsChevronDown } from "react-icons/bs";
import {
  activityGuidance,
  checklistItemAccordionId,
  explainNextTopicLock,
  /* Commenting the following code for revertfix.ray using AI Agent. 2026-08-07. Comment Start */
  // isChecklistComplete,
  /* Commenting end. ray using AI Agent. 2026-08-07 */
  jumpToAccordion,
  jumpToAccordionWithRetry,
  jumpToChecklistActivity,
  jumpToChecklistActivityWithRetry,
  resolveNextTopicClient,
  shortTopicLabel,
} from "../progressAccordion";
import { toNavigableProgressPath } from "../pagePathUtils";
/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */
import { CHAPTER_ROOT_PAGES, FOLDER_ROOT_PAGES } from "../chapterRootPages";
import { normalizePageSlug } from "../progressApi";
/* Code end. ray using AI Agent. 2026-08-07 */
import { useProgressOptional } from "../context/ProgressContext";

/* Commenting the following code for progress.ray using AI Agent. 28/07/2026. Comment Start */
// Local jumpToAccordion moved to progressAccordion.js (shared with ProgressContext resume).
// function jumpToAccordion(accordionId, fallbackLabel) { ... }
/* Commenting end. ray using AI Agent. 28/07/2026 */

const SidebarProgress = ({
  progress: progressProp,
  onResume: onResumeProp,
  progressStatus: statusProp,
  progressError: errorProp,
  onRetry: onRetryProp,
  /* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
  // Controlled expand state (owned by Resside) so menu remounts do not re-open the rail.
  railExpanded: railExpandedProp,
  onRailExpandedChange,
  /* Code end. ray using AI Agent. 28/07/2026 */
}) => {
  const ctx = useProgressOptional();
  const progress = progressProp ?? ctx?.progress ?? null;
  /* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
  const lockMap = ctx?.lockMap ?? null;
  /* Code end. ray using AI Agent. 28/07/2026 */
  const onResume = onResumeProp ?? ctx?.onResume;
  const progressStatus = statusProp ?? ctx?.progressStatus ?? "idle";
  const progressError = errorProp ?? ctx?.progressError ?? null;
  const onRetry = onRetryProp ?? ctx?.retryProgress;
  const navigate = useNavigate();
  const location = useLocation();
  const panelId = useId();

  /* Commenting the following code for progress.ray using AI Agent. 28/07/2026. Comment Start */
  // Local railExpanded remounted (and re-expanded) on every menu click because
  // MenuItemComponent is defined inside Resside. Expand state is now controlled by parent.
  // const [railExpanded, setRailExpanded] = useState(() =>
  //   typeof window !== "undefined" ? window.innerWidth >= 768 : true
  // );
  // useEffect(() => {
  //   const onResize = () => {
  //     if (window.innerWidth >= 768) setRailExpanded(true);
  //   };
  //   window.addEventListener("resize", onResize);
  //   return () => window.removeEventListener("resize", onResize);
  // }, []);
  /* Commenting end. ray using AI Agent. 28/07/2026 */
  /* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  const isControlled = typeof railExpandedProp === "boolean";
  const railExpanded = isControlled ? railExpandedProp : uncontrolledExpanded;
  const setRailExpanded = (next) => {
    const value = typeof next === "function" ? next(railExpanded) : next;
    if (!isControlled) setUncontrolledExpanded(value);
    onRailExpandedChange?.(value);
  };
  /* Code end. ray using AI Agent. 28/07/2026 */

  /* Added the following code for route_load.ray using AI Agent. Aug 12, 2026. Code Start */
  if ((progressStatus === "loading" || progressStatus === "idle") && !progress) {
  /* Code end. ray using AI Agent. Aug 12, 2026 */
  /* Commenting the following code for route_load.ray using AI Agent. Aug 12, 2026. Comment Start */
  // if (progressStatus === "loading" && !progress) {
  /* Commenting end. ray using AI Agent. Aug 12, 2026 */
    return (
      <div
        className="mt-1 px-1"
        role="status"
        aria-live="polite"
        aria-label="Loading your progress"
      >
        <p className="text-[11px] font-semibold text-[#044661] tracking-wide">
          YOUR PROGRESS
        </p>
        <div className="mt-2 animate-pulse space-y-2">
          <div className="h-10 rounded-lg bg-slate-200" />
          <div className="h-16 rounded-lg bg-slate-200" />
        </div>
      </div>
    );
  }

  if (progressStatus === "error" && !progress) {
    return (
      <div className="mt-1 px-1" role="alert">
        <p className="text-[11px] font-semibold text-[#044661] tracking-wide">
          YOUR PROGRESS
        </p>
        <p className="mt-2 text-[11px] text-red-700">
          {progressError || "Progress unavailable."}
        </p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 w-full rounded-md border border-[#044661] px-2 py-1.5 text-[11px] font-semibold text-[#044661]"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (!progress) return null;

  const {
    chapterLabel,
    chapterPct = 0,
    pagesDone = 0,
    totalPages = 0,
    /* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
    hubCoverage,
    /* Code end. ray using AI Agent. 28/07/2026 */
    /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
    videoCoverage,
    /* Code end. ray using AI Agent. 30/07/2026 */
    sectionLabel,
    sectionPages = [],
    resume,
    checklist = [],
    /* Commenting the following code for revertfix.ray using AI Agent. 2026-08-07. Comment Start */
    // checklistSummary,
    /* Commenting end. ray using AI Agent. 2026-08-07 */
    currentPage,
    completionPolicy,
    nextTopic: serverNextTopic,
    /* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */
    folderOverview = false,
    chapterOverview = false,
    /* Code end. ray using AI Agent. 2026-08-07 */
  } = progress;

  /* Added the following code for progress.ray using AI Agent. 29/07/2026. Code Start */
  // Normalize every rail link onto the new ch-* / menu-slug URLs (MenuSlugRouter).
  const navPath = (pathOrSlug) => {
    if (!pathOrSlug) return pathOrSlug;
    return toNavigableProgressPath(pathOrSlug) || pathOrSlug;
  };
  const currentPageNav = currentPage
    ? {
        ...currentPage,
        /* Added the following code for 1.2rmv.ray using AI Agent. 2026-08-24. Code Start */
        label:
          {
            "1.3 The Five Eras of NLP": "1.2 The Five Eras of NLP",
            "1.4 The NLP Pipeline": "1.3 The NLP Pipeline",
            "The Five Eras of NLP": "1.2 The Five Eras of NLP",
            "The NLP Pipeline": "1.3 The NLP Pipeline",
          }[currentPage.label] || currentPage.label,
        /* Code end. ray using AI Agent. 2026-08-24 */
        path: navPath(currentPage.path || currentPage.unitKey),
      }
    : null;
  const resumeNav = resume
    ? {
        ...resume,
        path: navPath(resume.path || resume.pageSlug),
      }
    : null;
  /* Added the following code for 1.2rmv.ray using AI Agent. 2026-08-24. Code Start */
  const CH1_PROGRESS_RENUMBER = {
    "1.3 The Five Eras of NLP": "1.2 The Five Eras of NLP",
    "1.4 The NLP Pipeline": "1.3 The NLP Pipeline",
    "The Five Eras of NLP": "1.2 The Five Eras of NLP",
    "The NLP Pipeline": "1.3 The NLP Pipeline",
  };
  /* Added the following code for menu_cleanup.ray using AI Agent. 04/09/2026. Code Start */
  const MENU_CLEANUP_PROGRESS_RENUMBER = {
    "4.6 Major LLM Families": "4.5 Major LLM Families",
    "4.7 Model Evaluation": "4.6 Model Evaluation",
    "4.8 Applications of LLMs": "4.7 Applications of LLMs",
    Agents: "4.8 Agents",
    "6.3 Hallucination Mitigation": "6.2 Hallucination Mitigation",
    "6.4 Bias & Ethics": "6.3 Bias & Ethics",
    "6.5 Future Directions": "6.4 Future Directions",
  };
  const PROGRESS_LABEL_RENUMBER = {
    ...CH1_PROGRESS_RENUMBER,
    ...MENU_CLEANUP_PROGRESS_RENUMBER,
  };
  const isRetiredProgressRoute = (slugOrPath, label) => {
    const s = String(slugOrPath || "").toLowerCase();
    const n = String(label || "").toLowerCase();
    if (s.includes("12-applications-of-nlp") || s.includes("applications-of-nlp")) {
      return true;
    }
    if (s.includes("45-alignment-tuning-rlhf") || /4\.5\s*alignment tuning/.test(n)) {
      return true;
    }
    if (s.includes("56-classic-tasks") || /5\.6\s*classic tasks/.test(n)) {
      return true;
    }
    if (s.includes("62-explainable-ai") || /6\.2\s*explainable ai/.test(n)) {
      return true;
    }
    if (
      s.includes("narrative-links-to-era-4") ||
      s.includes("synthesis-content") ||
      s.includes("narrative-bridge-into-generative") ||
      s.includes("63-hallucination-mitigation-cross-link") ||
      s.includes("64-bias-ethics-cross-link") ||
      s.includes("65-future-directions-synthesis")
    ) {
      return true;
    }
    return false;
  };
  const sectionPagesNav = sectionPages
    .filter(
      (p) =>
        !isRetiredProgressRoute(p.path, p.label) &&
        !isRetiredProgressRoute(p.unitKey, p.label)
    )
    .map((p) => ({
      ...p,
      label: PROGRESS_LABEL_RENUMBER[p.label] || p.label,
      path: p.path ? navPath(p.path) : p.path,
    }));
  const hubsNav = (hubCoverage?.hubs || [])
    .filter(
      (h) =>
        !isRetiredProgressRoute(h.path, h.label) &&
        !isRetiredProgressRoute(h.unitKey, h.label)
    )
    .map((h) => ({
      ...h,
      label: PROGRESS_LABEL_RENUMBER[h.label] || h.label,
      path: h.path ? navPath(h.path) : h.path || (h.unitKey ? navPath(h.unitKey) : null),
    }));
  /* Code end. ray using AI Agent. 04/09/2026 */
  /* Commenting the following code for menu_cleanup.ray using AI Agent. 04/09/2026. Comment Start */
  // const isRetiredProgressRoute = (slugOrPath) =>
  //   String(slugOrPath || "").includes("12-applications-of-nlp");
  // const sectionPagesNav = sectionPages
  //   .filter(
  //     (p) =>
  //       !isRetiredProgressRoute(p.path) && !isRetiredProgressRoute(p.unitKey)
  //   )
  //   .map((p) => ({
  //     ...p,
  //     label: CH1_PROGRESS_RENUMBER[p.label] || p.label,
  //     path: p.path ? navPath(p.path) : p.path,
  //   }));
  // const hubsNav = (hubCoverage?.hubs || [])
  //   .filter(
  //     (h) =>
  //       !isRetiredProgressRoute(h.path) && !isRetiredProgressRoute(h.unitKey)
  //   )
  //   .map((h) => ({
  //     ...h,
  //     label: CH1_PROGRESS_RENUMBER[h.label] || h.label,
  //     path: h.path ? navPath(h.path) : h.path || (h.unitKey ? navPath(h.unitKey) : null),
  //   }));
  /* Commenting end. ray using AI Agent. 04/09/2026 */
  /* Code end. ray using AI Agent. 2026-08-24 */
  /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
  // Only unfinished content / video on the current stage page (not every chapter item).
  const nextVideoRaw = videoCoverage?.nextVideo || null;
  const nextVideo = nextVideoRaw
    ? {
        ...nextVideoRaw,
        path: nextVideoRaw.path
          ? navPath(nextVideoRaw.path)
          : nextVideoRaw.pageSlug
            ? navPath(nextVideoRaw.pageSlug)
            : null,
      }
    : null;
  const nextContentRaw = videoCoverage?.nextContent || null;
  const nextContent = nextContentRaw
    ? {
        ...nextContentRaw,
        path: nextContentRaw.path
          ? navPath(nextContentRaw.path)
          : nextContentRaw.pageSlug
            ? navPath(nextContentRaw.pageSlug)
            : null,
      }
    : null;
  /* Code end. ray using AI Agent. 30/07/2026 */
  /* Code end. ray using AI Agent. 29/07/2026 */

  const doneCount = sectionPagesNav.filter((p) => p.state === "done").length;
  /* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
  const hubsVisited = Number(hubCoverage?.hubsVisited) || 0;
  const hubsTotal = Number(hubCoverage?.hubsTotal) || 0;
  /* Commenting the following code for progress.ray using AI Agent. 29/07/2026. Comment Start */
  // const unvisitedHubs = (hubCoverage?.hubs || []).filter((h) => !h.visited);
  /* Commenting end. ray using AI Agent. 29/07/2026 */
  /* Commenting the following code for video.ray using AI Agent. 30/07/2026. Comment Start */
  // Long hubs list removed — next-stage path hints replace it.
  // const unvisitedHubs = hubsNav.filter((h) => !h.visited);
  /* Commenting end. ray using AI Agent. 30/07/2026 */
  const lessonsDone = Number(pagesDone) || 0;
  const lessonsTotal = Number(totalPages) || 0;
  /* Code end. ray using AI Agent. 28/07/2026 */
  /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
  // Collapsed badge: only when the current stage still needs content or video.
  const showNextContentHint = !!nextContent;
  const showNextVideoHint = !!nextVideo;
  /* Commenting the following code for video.ray using AI Agent. 30/07/2026. Comment Start */
  // const videosDone = Number(videoCoverage?.videosDone) || 0;
  // const videosTotal = Number(videoCoverage?.videosTotal) || 0;
  // const videosRemaining = Number(videoCoverage?.videosRemaining) || 0;
  /* Commenting end. ray using AI Agent. 30/07/2026 */
  /* Code end. ray using AI Agent. 30/07/2026 */
  /* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */
  // Leaf pages: THIS MODULE = leaf activities only (exclude hub Part / Part video rows).
  // Root/subfolder hubs: never show a rebound child-leaf checklist; only this hub's tasks.
  const isHubChecklistItem = (item) => {
    const id = String(item?.id || "").toLowerCase();
    const kind = String(item?.kind || "").toLowerCase();
    return (
      kind === "hub" ||
      id.startsWith("hub_visit:") ||
      id.startsWith("hub_video:")
    );
  };
  const routeBaseSlug = normalizePageSlug(
    String(location.pathname || "")
      .replace(
        /\/(what-is-it|video|test-it|check-it|try-it|quiz)\/?$/i,
        ""
      )
      .replace(/^\/+|\/+$/g, "")
  );
  const onHubOverview =
    !!folderOverview ||
    !!chapterOverview ||
    !!CHAPTER_ROOT_PAGES[routeBaseSlug] ||
    !!FOLDER_ROOT_PAGES[routeBaseSlug];
  const hubItemMatchesRoute = (item) => {
    if (!routeBaseSlug || !isHubChecklistItem(item)) return false;
    const id = String(item?.id || "").toLowerCase();
    const itemSlug = normalizePageSlug(item?.pageSlug || item?.path || "");
    if (itemSlug && itemSlug === routeBaseSlug) return true;
    const suffix = id.includes(":") ? id.slice(id.indexOf(":") + 1) : "";
    return !!suffix && normalizePageSlug(suffix) === routeBaseSlug;
  };
  const moduleChecklist = onHubOverview
    ? (checklist || []).filter((item) => hubItemMatchesRoute(item))
    : (checklist || []).filter((item) => !isHubChecklistItem(item));
  const moduleSectionTitle = onHubOverview ? "THIS FOLDER" : "THIS MODULE";
  const moduleSectionLabel = onHubOverview
    ? CHAPTER_ROOT_PAGES[routeBaseSlug]?.pageName ||
      FOLDER_ROOT_PAGES[routeBaseSlug]?.pageName ||
      currentPageNav?.label
    : currentPageNav?.label;
  const pendingModuleChecklist = moduleChecklist.filter((item) => !item.done);
  const allChecklistDone =
    moduleChecklist.length > 0 && pendingModuleChecklist.length === 0;
  const nextRequiredItem = pendingModuleChecklist[0] || null;
  const moduleDoneCount = moduleChecklist.filter((c) => c.done).length;
  const moduleChecklistSummary =
    moduleChecklist.length > 0
      ? `${moduleDoneCount} of ${moduleChecklist.length}`
      : null;
  /* Code end. ray using AI Agent. 2026-08-07 */
  /* Commenting the following code for revertfix.ray using AI Agent. 2026-08-07. Comment Start */
  // const allChecklistDone = isChecklistComplete(checklist) && checklist.length > 0;
  // const nextRequiredItem = checklist.find((item) => !item.done);
  /* Commenting end. ray using AI Agent. 2026-08-07 */
  /* Commenting the following code for progress.ray using AI Agent. 29/07/2026. Comment Start */
  // const nextTopic =
  //   serverNextTopic ||
  //   resolveNextTopicClient({ sectionPages, currentPage, resume });
  /* Commenting end. ray using AI Agent. 29/07/2026 */
  /* Added the following code for progress.ray using AI Agent. 29/07/2026. Code Start */
  const nextTopicRaw =
    serverNextTopic ||
    resolveNextTopicClient({
      sectionPages: sectionPagesNav,
      currentPage: currentPageNav,
      resume: resumeNav,
    });
  const nextTopic = nextTopicRaw
    ? {
        ...nextTopicRaw,
        path: nextTopicRaw.path ? navPath(nextTopicRaw.path) : nextTopicRaw.path,
      }
    : null;
  /* Code end. ray using AI Agent. 29/07/2026 */
  const clampedPct = Math.min(100, Math.max(0, Number(chapterPct) || 0));
  /* Commenting the following code for revertfix.ray using AI Agent. 2026-08-07. Comment Start */
  // const incompleteCount = pendingModuleChecklist.length;
  // const incompleteCount = checklist.filter((c) => !c.done).length;
  /* Commenting end. ray using AI Agent. 2026-08-07 */

  // One primary CTA: unfinished activity → Next action; else unlocked next topic; else Resume if distinct.
  const resumeSameAsNext =
    resumeNav?.path &&
    nextTopic?.path &&
    !nextTopic.locked &&
    resumeNav.path === nextTopic.path;
  const showResume =
    !!resumeNav?.path &&
    !resumeSameAsNext &&
    (!nextTopic || nextTopic.locked || !!nextRequiredItem);

  /* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
  // Prefer in-page accordion jump; after route change ProgressContext applies resumeState.accordionId.
  const handleChecklistClick = async (item) => {
    /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
    // Path hub items navigate to the hub (visit) or hub video with autoplay.
    const itemId = String(item?.id || "").toLowerCase();
    if (itemId.startsWith("hub_visit:") || itemId.startsWith("hub_video:")) {
      const hubPath = navPath(item.path || item.pageSlug);
      if (!hubPath) return;
      navigate(hubPath, {
        state: {
          pageName: item.label,
          resumeState: itemId.startsWith("hub_video:")
            /* Added the following code for testit.ray using AI Agent. 2026-08-07. Code Start */
            ? { accordionId: "video", section: "video", autoplayVideo: false }
            /* Code end. ray using AI Agent. 2026-08-07 */
            /* Commenting the following code for testit.ray using AI Agent. 2026-08-07. Comment Start */
            // ? { accordionId: "video", section: "video", autoplayVideo: true }
            /* Commenting end. ray using AI Agent. 2026-08-07 */
            : null,
        },
      });
      return;
    }
    /* Code end. ray using AI Agent. 30/07/2026 */

    /* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
    // Pending video → open header player; other items keep accordion jump.
    if (itemId === "video") {
      if (jumpToChecklistActivity(item)) return;
      const openedVideo = await jumpToChecklistActivityWithRetry(item, {
        attempts: 12,
        delayMs: 100,
      });
      if (openedVideo) return;
    }
    /* Code end. ray using AI Agent. 28/07/2026 */

    const accordionId = checklistItemAccordionId(item);
    if (jumpToAccordion(accordionId, item.label)) return;

    /* Commenting the following code for progress.ray using AI Agent. 29/07/2026. Comment Start */
    // Compared raw location to navigable path — /whatis-NLP vs /ch-1-… looked like a different page.
    // const targetPath = toNavigableProgressPath(currentPage?.path || resume?.path);
    // const here = location.pathname || "";
    // const samePage = !!targetPath && (here === targetPath || ...);
    /* Commenting end. ray using AI Agent. 29/07/2026 */
    /* Added the following code for progress.ray using AI Agent. 29/07/2026. Code Start */
    const targetPath = navPath(
      currentPageNav?.path || resumeNav?.path || currentPageNav?.unitKey
    );
    const here = location.pathname || "";
    const hereNav = navPath(here);
    const strip = (p) => String(p || "").replace(/\/+$/, "");
    const samePage =
      !!targetPath &&
      !!hereNav &&
      (strip(hereNav) === strip(targetPath) || strip(here) === strip(targetPath));
    /* Code end. ray using AI Agent. 29/07/2026 */

    // Already on the lesson (or accordion still mounting): retry before navigating.
    if (samePage || here) {
      const opened = await jumpToAccordionWithRetry(accordionId, item.label, {
        attempts: samePage ? 10 : 3,
        delayMs: 100,
      });
      if (opened) return;
    }

    if (!targetPath) return;
    navigate(targetPath, {
      state: {
        pageName: currentPageNav?.label || resumeNav?.label,
        resumeState: {
          accordionId,
          section: item.id,
        },
      },
    });
  };
  /* Code end. ray using AI Agent. 28/07/2026 */

  /* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
  // Nested under Dashboard: header row toggles details on all breakpoints.
  return (
    <div className="mt-1 px-1">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left hover:bg-gray-200"
        onClick={() => setRailExpanded((v) => !v)}
        aria-expanded={railExpanded}
        aria-controls={panelId}
      >
        <span className="text-[11px] font-semibold text-[#044661] tracking-wide">
          YOUR PROGRESS
          {/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */}
          {!railExpanded && moduleChecklist.length > 0 ? (
            <span className="ml-1 font-normal text-gray-500 normal-case tracking-normal">
              ({moduleDoneCount}/{moduleChecklist.length})
            </span>
          ) : null}
          {/* Code end. ray using AI Agent. 2026-08-07 */}
          {/* Commenting the following code for revertfix.ray using AI Agent. 2026-08-07. Comment Start */}
          {/* {!railExpanded && incompleteCount > 0 ? (
            <span className="ml-1 font-normal text-gray-500 normal-case tracking-normal">
              ({checklist.length - incompleteCount}/{checklist.length})
            </span>
          ) : null} */}
          {/* Commenting end. ray using AI Agent. 2026-08-07 */}
          {/* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */}
          {!railExpanded && (showNextContentHint || showNextVideoHint) ? (
            <span className="ml-1 font-normal text-amber-700 normal-case tracking-normal">
              ·{" "}
              {[
                showNextContentHint ? "view content" : null,
                showNextVideoHint ? "watch video" : null,
              ]
                .filter(Boolean)
                .join(", ")}
            </span>
          ) : null}
          {/* Code end. ray using AI Agent. 30/07/2026 */}
        </span>
        <BsChevronDown
          className={`shrink-0 text-[#044661] transition-transform ${
            railExpanded ? "rotate-180" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      {!railExpanded && nextRequiredItem ? (
        <div className="mt-1 rounded-md bg-cyan-50 border border-cyan-100 px-2 py-1.5">
          <p className="text-[10px] font-semibold text-[#044661]">NEXT ACTION</p>
          <p className="text-[10px] text-gray-700 mt-0.5">
            {activityGuidance(nextRequiredItem)}
          </p>
        </div>
      ) : null}

      <div
        id={panelId}
        className={railExpanded ? "block" : "hidden"}
        role="region"
        aria-label="Your learning progress details"
      >
          <div className="mt-2 bg-slate-100 rounded-lg p-2.5 border border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-700 truncate pr-2">
                {chapterLabel || "Chapter"} path
              </span>
              <span className="text-[11px] font-semibold text-[#044661]">
                {clampedPct}%
              </span>
            </div>
            <div
              className="mt-1.5 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={clampedPct}
              aria-label={`${chapterLabel || "Chapter"} progress`}
            >
              <div
                className="h-full rounded-full bg-green-500 transition-all"
                style={{ width: `${clampedPct}%` }}
              />
            </div>
            <p className="sr-only">
              {clampedPct} percent of chapter complete
            </p>
            {/* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */}
            {/* Lesson % stays primary; hub visits are path coverage only. */}
            {(lessonsTotal > 0 || hubsTotal > 0) && (
              <p className="mt-1.5 text-[10px] text-gray-600">
                {[
                  lessonsTotal > 0
                    ? `Lessons ${lessonsDone}/${lessonsTotal}`
                    : null,
                  hubsTotal > 0
                    ? `Hubs ${hubsVisited}/${hubsTotal} visited`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
            {/* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */}
            {/* Path order: chapter root / subfolder / topic — one next step at a time. */}
            {nextContent ? (
              <div className="mt-1.5">
                <p className="text-[10px] font-semibold text-amber-800">
                  {nextContent.kind === "hub"
                    ? "Next part to view"
                    : "Next content to view"}
                </p>
                {nextContent.path ? (
                  <Link
                    to={nextContent.path}
                    state={{
                      pageName: nextContent.label,
                      resumeState:
                        nextContent.kind === "hub"
                          ? null
                          : {
                              accordionId: "what_is_it",
                              section: "what_is_it",
                            },
                    }}
                    className="mt-0.5 block truncate text-[10px] text-[#0891b2] underline"
                  >
                    {nextContent.label}
                  </Link>
                ) : (
                  <p className="mt-0.5 truncate text-[10px] text-gray-500">
                    {nextContent.label}
                  </p>
                )}
              </div>
            ) : null}
            {nextVideo ? (
              <div className="mt-1.5">
                <p className="text-[10px] font-semibold text-amber-800">
                  {nextVideo.kind === "hub"
                    ? "Next part video to watch"
                    : "Next video to progress"}
                </p>
                {nextVideo.path ? (
                  <Link
                    to={nextVideo.path}
                    state={{
                      pageName: nextVideo.label,
                      resumeState: {
                        accordionId: "video",
                        section: "video",
                        /* Added the following code for testit.ray using AI Agent. 2026-08-07. Code Start */
                        autoplayVideo: false,
                        /* Code end. ray using AI Agent. 2026-08-07 */
                        /* Commenting the following code for testit.ray using AI Agent. 2026-08-07. Comment Start */
                        // autoplayVideo: true,
                        /* Commenting end. ray using AI Agent. 2026-08-07 */
                      },
                    }}
                    className="mt-0.5 block truncate text-[10px] text-[#0891b2] underline"
                  >
                    {nextVideo.label}
                  </Link>
                ) : (
                  <p className="mt-0.5 truncate text-[10px] text-gray-500">
                    {nextVideo.label}
                  </p>
                )}
              </div>
            ) : null}
            {/* Commenting the following code for video.ray using AI Agent. 30/07/2026. Comment Start */}
            {/* Long "Hubs still to visit" list — replaced by next-stage path hints above.
            {hubsTotal > 0 && unvisitedHubs.length > 0 ? ( ... ) : ...}
            */}
            {/* Commenting end. ray using AI Agent. 30/07/2026 */}
            {/* Code end. ray using AI Agent. 30/07/2026 */}
            {/* Code end. ray using AI Agent. 28/07/2026 */}
            {sectionLabel ? (
              <p className="text-[10px] text-gray-500 mt-1.5 truncate">
                {sectionLabel}
              </p>
            ) : null}
            {sectionPagesNav.length > 0 ? (
              <p className="text-[10px] text-gray-500">
                {doneCount} of {sectionPagesNav.length}{" "}
                {sectionPagesNav.length === 1 ? "topic" : "topics"} in section
              </p>
            ) : null}
          </div>

          {/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */}
          {moduleChecklist.length > 0 ? (
            <div className="mt-3 rounded-lg border border-[#0891b2]/20 bg-white p-2.5">
              <p className="text-[11px] font-semibold text-[#044661]">
                {moduleSectionTitle}
              </p>
              {moduleSectionLabel ? (
                <p className="text-[10px] text-gray-600 mt-0.5 truncate">
                  {moduleSectionLabel}
                </p>
              ) : null}
              {moduleChecklistSummary ? (
                <p className="text-[10px] text-gray-500 mt-1">
                  {moduleChecklistSummary} activities
                  {pendingModuleChecklist.length > 0
                    ? ` · ${pendingModuleChecklist.length} pending`
                    : " · complete"}
                </p>
              ) : null}
              {/* Code end. ray using AI Agent. 2026-08-07 */}
              {/* Commenting the following code for revertfix.ray using AI Agent. 2026-08-07. Comment Start */}
              {/* {checklist.length > 0 ? (
            <div className="mt-3 rounded-lg border border-[#0891b2]/20 bg-white p-2.5">
              <p className="text-[11px] font-semibold text-[#044661]">
                THIS MODULE
              </p>
              {currentPageNav?.label ? (
                <p className="text-[10px] text-gray-600 mt-0.5 truncate">
                  {currentPageNav.label}
                </p>
              ) : null}
              {checklistSummary ? (
                <p className="text-[10px] text-gray-500 mt-1">
                  {checklistSummary} activities
                </p>
              ) : null} */}
              {/* Commenting end. ray using AI Agent. 2026-08-07 */}

              {nextRequiredItem ? (
                <div className="mt-2 rounded-md bg-cyan-50 border border-cyan-100 px-2 py-1.5">
                  <p className="text-[10px] font-semibold text-[#044661]">
                    NEXT ACTION
                  </p>
                  <p className="text-[10px] text-gray-700 mt-0.5">
                    {activityGuidance(nextRequiredItem)}
                  </p>
                  <button
                    type="button"
                    className="mt-1.5 w-full text-center bg-[#0891b2] text-white rounded-md px-2 py-1.5 text-[11px] font-semibold hover:bg-[#0e7490]"
                    onClick={() => handleChecklistClick(nextRequiredItem)}
                  >
                    Go to {nextRequiredItem.label}
                  </button>
                </div>
              ) : null}

              {/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */}
              <ul className="mt-2 space-y-1.5" aria-label="Module checklist">
                {moduleChecklist.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-start gap-1.5 text-[11px] leading-snug"
                  >
                    <span
                      className={`shrink-0 mt-0.5 font-bold ${
                        item.done ? "text-green-600" : "text-gray-400"
                      }`}
                      aria-hidden="true"
                    >
                      {item.done ? "✓" : "○"}
                    </span>
                    <span className="sr-only">
                      {item.done ? "Complete" : "Incomplete"}
                    </span>
                    <button
                      type="button"
                      className={`text-left ${
                        item.done
                          ? "text-gray-700"
                          : "text-[#0891b2] underline"
                      }`}
                      onClick={() => handleChecklistClick(item)}
                      aria-label={`${item.done ? "Review" : "Complete"} ${item.label}`}
                      aria-current={
                        nextRequiredItem?.id === item.id ? "step" : undefined
                      }
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
              {/* Code end. ray using AI Agent. 2026-08-07 */}
              {/* Commenting the following code for revertfix.ray using AI Agent. 2026-08-07. Comment Start */}
              {/* <ul ...>{checklist.map(...)}</ul> */}
              {/* Commenting end. ray using AI Agent. 2026-08-07 */}

              {allChecklistDone ? (
                /* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
                <div className="mt-2 rounded-md bg-green-50 border border-green-200 p-2 text-[11px] text-gray-700">
                  {nextTopic?.locked ? (
                    <>
                      <p className="font-semibold text-[#044661]">
                        {/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */}
                        {onHubOverview
                          ? "Folder complete — next topic locked"
                          : "Module complete — next topic locked"}
                        {/* Code end. ray using AI Agent. 2026-08-07 */}
                      </p>
                      <p className="mt-0.5 text-amber-800">
                        {explainNextTopicLock({
                          checklistComplete: true,
                          incomplete: [],
                          nextTopic,
                          sectionPages: sectionPagesNav,
                          currentPage: currentPageNav,
                          lockMap,
                          completionPolicy,
                        })}
                      </p>
                      <Link
                        to="/learning-path"
                        className="mt-1.5 inline-block text-[#0891b2] underline"
                      >
                        See learning path
                      </Link>
                    </>
                  ) : nextTopic?.path ? (
                    <>
                      <p className="font-semibold text-[#044661]">
                        {/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */}
                        {onHubOverview ? "Folder complete" : "Module complete"}
                        {/* Code end. ray using AI Agent. 2026-08-07 */}
                      </p>
                      <p className="mt-0.5 text-gray-600">
                        {/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */}
                        {onHubOverview
                          ? "Open a topic in this folder to continue learning."
                          : `Leave this lesson from the bar below the content${
                              nextTopic.label
                                ? ` → ${shortTopicLabel(nextTopic.label, 28)}`
                                : "."
                            }`}
                        {/* Code end. ray using AI Agent. 2026-08-07 */}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-semibold text-[#044661]">
                        Section checklist complete
                      </p>
                      <p className="mt-0.5 text-gray-600">
                        Use Learning path in the bar below to pick another topic.
                      </p>
                    </>
                  )}
                </div>
                /* Code end. ray using AI Agent. 28/07/2026 */
              ) : (
                <p className="text-[10px] text-amber-700 mt-2">
                  {completionPolicy?.note ||
                    "Finish all items to unlock the next topic."}
                </p>
              )}

              {/* Commenting the following code for menu.ray using AI Agent. 28/07/2026. Comment Start */}
              {/* Duplicate leave-lesson CTA removed — footer owns Next / Learning path. */}
              {/* {!nextRequiredItem && nextTopic ? ( ... Continue to next topic ... ) : null} */}
              {/* Commenting end. ray using AI Agent. 28/07/2026 */}
            </div>
          ) : null}

          {/* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */}
          {/* No module checklist: sidebar still offers leave-lesson (footer is hidden). */}
          {/* Added the following code for revertfix.ray using AI Agent. 2026-08-07. Code Start */}
          {!moduleChecklist.length && nextTopic ? (
          /* Code end. ray using AI Agent. 2026-08-07 */
            nextTopic.locked || !nextTopic.path ? (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px]">
                <p className="font-semibold text-[#044661]">
                  Next: {shortTopicLabel(nextTopic.label || "topic", 28)} (locked)
                </p>
                <p className="mt-0.5 text-amber-800">
                  {explainNextTopicLock({
                    checklistComplete: true,
                    incomplete: [],
                    nextTopic,
                    sectionPages: sectionPagesNav,
                    currentPage: currentPageNav,
                    lockMap,
                    completionPolicy,
                  })}
                </p>
                <Link
                  to="/learning-path"
                  className="mt-1.5 inline-block text-[#0891b2] underline"
                >
                  See learning path
                </Link>
              </div>
            ) : (
              <Link
                to={nextTopic.path}
                state={{ pageName: nextTopic.label }}
                className="mt-3 block w-full text-center bg-[#0891b2] text-white rounded-md px-2 py-1.5 text-[11px] font-semibold hover:bg-[#0e7490]"
              >
                Next: {shortTopicLabel(nextTopic.label)}
              </Link>
            )
          ) : null}
          {/* Code end. ray using AI Agent. 28/07/2026 */}
          {/* Commenting the following code for menu.ray using AI Agent. 28/07/2026. Comment Start */}
          {/* {!checklist.length && nextTopic ? ( disabled Next topic / Continue to next topic ) : null} */}
          {/* Commenting end. ray using AI Agent. 28/07/2026 */}

          {sectionPagesNav.length > 0 ? (
            <>
              <p className="text-[11px] font-semibold text-[#044661] mt-3 mb-1.5">
                THIS SECTION
              </p>
              <ul className="space-y-0.5" aria-label="Topics in this section">
                {sectionPagesNav.map((p) => {
                  const locked = p.state === "locked";
                  const isCurrent =
                    currentPageNav?.unitKey &&
                    p.unitKey === currentPageNav.unitKey;
                  const done = p.state === "done";
                  const rowClass = locked
                    ? "text-gray-500 opacity-75 border border-dashed border-gray-300"
                    : isCurrent
                      ? "bg-[#0891b2] text-white"
                      : done
                        ? "text-[#044661] hover:bg-gray-100"
                        : "text-[#044661] hover:bg-gray-100";

                  const inner = (
                    <div
                      className={`rounded-md px-2 py-1.5 text-[11px] leading-snug ${rowClass}`}
                      aria-disabled={locked || undefined}
                      aria-current={isCurrent ? "page" : undefined}
                    >
                      <div className="flex gap-1.5 items-start">
                        <span className="shrink-0 mt-0.5" aria-hidden="true">
                          {done ? "✓" : locked ? "◌" : "●"}
                        </span>
                        <span className="sr-only">
                          {done
                            ? "Completed"
                            : locked
                              ? "Locked"
                              : isCurrent
                                ? "Current"
                                : "Available"}
                        </span>
                        <span className="min-w-0">{p.label}</span>
                      </div>
                      {locked ? (
                        <p
                          className={`text-[9px] mt-0.5 ml-3.5 ${
                            isCurrent ? "text-white/85" : "text-gray-500"
                          }`}
                        >
                          Locked — finish prior module checklist
                        </p>
                      ) : null}
                    </div>
                  );

                  if (locked || !p.path) {
                    return (
                      <li key={p.unitKey || p.label}>
                        <div role="listitem">{inner}</div>
                      </li>
                    );
                  }

                  return (
                    <li key={p.unitKey || p.label}>
                      <Link to={p.path} state={{ pageName: p.label }}>
                        {inner}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}

          {showResume ? (
            <button
              type="button"
              onClick={onResume}
              disabled={!resumeNav?.path}
              className="mt-3 w-full bg-[#044661] text-white rounded-md px-2.5 py-2 text-[11px] font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {resumeNav?.nextChecklistItem?.label
                ? `Resume: ${resumeNav.nextChecklistItem.label}`
                : "Resume current topic"}
            </button>
          ) : null}
      </div>
    </div>
  );
};

export default SidebarProgress;
/* Code end. Ray using AI Agent. 27/07/2026 */
